import { isColorVisible } from './utils';
import { RGBToHSL } from './colorConverter';
export class UnableToPickColorError extends Error {
  constructor(m) {
    super(m);

    // Set the prototype explicitly.
    // https://github.com/facebook/jest/issues/8279
    Object.setPrototypeOf(this, UnableToPickColorError.prototype);
  }
}
function convertHSLColorsToColorsGroups(colors) {
  const colorsGroups = {
    red: [],
    yellow: [],
    green: [],
    cyan: [],
    blue: [],
    pink: []
  };
  for (const color of colors) {
    const [h] = color;
    switch (true) {
      case h <= 30:
      case h > 330:
        colorsGroups.red.push(color);
        break;
      case h <= 90:
        colorsGroups.yellow.push(color);
        break;
      case h <= 150:
        colorsGroups.green.push(color);
        break;
      case h <= 210:
        colorsGroups.cyan.push(color);
        break;
      case h <= 270:
        colorsGroups.blue.push(color);
        break;
      case h <= 330:
        colorsGroups.pink.push(color);
        break;
      default:
    }
  }
  return colorsGroups;
}
function getAverageColorFromColorsGroup(colorsGroup) {
  // Only for hsl color
  const colorsLength = colorsGroup.length;
  let avgH = 0;
  let avgS = 0;
  let avgL = 0;
  let isRed = false;
  for (const [h, s, l] of colorsGroup) {
    if (h <= 30) {
      // Red between 0 and 30
      avgH += h + 300; // To set red color between 300 and 360
      isRed = true;
    } else {
      avgH += h;
    }
    avgL += l;
    avgS += s;
  }
  avgH = Math.floor(avgH / colorsLength);
  avgL = Math.floor(avgL / colorsLength);
  avgS = Math.floor(avgS / colorsLength);
  if (isRed && avgH < 330) {
    avgH -= 300;
  }
  return [avgH, avgS, avgL];
}
function getAccuracyByWidth(size) {
  switch (true) {
    case size < 60:
      return 1;
    case size >= 60 && size < 160:
      return 2;
    case size >= 160 && size < 260:
      return 4;
    case size >= 260:
      return 6;
    default:
      return 1;
  }
}
function getImageColors(data) {
  const accuracy = getAccuracyByWidth(data.width);
  const nbColorParams = 4; // 4 = r, g, b, a
  const nbPixelToPickPerLine = Math.floor(data.width);
  const length = data.data ? data.data.length : 0;
  const nbLinesToJump = accuracy === 1 ? 0 : Math.floor(data.height / nbPixelToPickPerLine); // Height = nb lines
  const lineLength = data.width * nbColorParams;
  const spaceHorizontal = Math.floor(lineLength / nbPixelToPickPerLine); // Horizontal space between two pixels to pick
  const spaceVertical = Math.floor(nbLinesToJump * lineLength); // Vertical space between two pixels to pick on change of line
  const colors = [];
  let i = 0 - spaceHorizontal;
  let line = 0;
  while ((i += spaceHorizontal) < length) {
    const hsl = RGBToHSL([data.data[i], data.data[i + 1], data.data[i + 2]]);
    colors.push(hsl);
    const nextPixel = i + spaceHorizontal * nbColorParams;
    const nextLine = Math.floor(nextPixel / (data.width * nbColorParams));
    if (nextLine !== line) {
      // The line will change because of the next pixel
      i += spaceVertical;
      line = Math.floor(i / (data.width * nbColorParams));
    }
  }
  return colors;
}
export function pickColor(imgDomElem) {
  const canvas = document.createElement('canvas');
  canvas.height = imgDomElem.naturalHeight || imgDomElem.offsetHeight || imgDomElem.height;
  canvas.width = imgDomElem.naturalWidth || imgDomElem.offsetWidth || imgDomElem.width;
  return pickColorWithProvidedCanvas(imgDomElem, canvas);
}
export function pickColorWithProvidedCanvas(imageElement, canvas) {
  let data = null;
  const height = imageElement.naturalHeight || imageElement.height;
  const width = imageElement.naturalWidth || imageElement.width;
  const context = canvas.getContext && canvas.getContext('2d');
  if (!context) {
    throw new UnableToPickColorError('Cannot pick color');
  }
  context.drawImage(imageElement, 0, 0);
  try {
    data = context.getImageData(0, 0, width, height);
  } catch (a) {
    // Security error, img on diff domain
    throw new UnableToPickColorError('Cannot get image data');
  }
  const imageColors = getImageColors(data);
  const visibleColors = imageColors.filter(color => isColorVisible(color));
  if (visibleColors.length === 0) {
    throw new UnableToPickColorError('Image is not enough colored');
  }
  const colorsGroups = convertHSLColorsToColorsGroups(visibleColors);
  const mainColorKey = Object.keys(colorsGroups).reduce((acc, curr) => colorsGroups[curr].length > colorsGroups[acc].length ? curr : acc);
  return {
    color: getAverageColorFromColorsGroup(colorsGroups[mainColorKey]),
    visiblePixelsPercentage: Math.floor(visibleColors.length / imageColors.length * 100)
  };
}
//# sourceMappingURL=colorPicker.js.map